/**
 * Error model for every call that leaves the browser.
 *
 * The platform API documents Problem Details style errors (code, status, detail,
 * request_id, field errors). Anything that does not match that shape — an HTML
 * error page, a gateway timeout, a connection reset — is still normalised into an
 * ApiError so that no call site has to inspect a raw Response or guess.
 */

export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'not_implemented'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'invalid_response'
  | 'unknown';

export interface FieldIssue {
  readonly field: string;
  readonly message: string;
}

export interface ApiErrorInit {
  readonly kind: ApiErrorKind;
  readonly message: string;
  readonly method: string;
  readonly path: string;
  readonly status?: number;
  readonly code?: string;
  readonly requestId?: string;
  readonly fieldIssues?: readonly FieldIssue[];
  readonly retryable?: boolean;
  readonly cause?: unknown;
}

const RETRYABLE_KINDS: ReadonlySet<ApiErrorKind> = new Set<ApiErrorKind>([
  'network',
  'rate_limited',
  'server',
  'unknown',
]);

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly method: string;
  readonly path: string;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly requestId: string | undefined;
  readonly fieldIssues: readonly FieldIssue[];
  readonly retryable: boolean;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.kind = init.kind;
    this.method = init.method;
    this.path = init.path;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.fieldIssues = init.fieldIssues ?? [];
    this.retryable = init.retryable ?? RETRYABLE_KINDS.has(init.kind);
  }

  /** True when the session is missing, expired or revoked. */
  get isAuthFailure(): boolean {
    return this.kind === 'unauthorized';
  }

  /** True when the endpoint is absent rather than the data being absent. */
  get isProbablyUnimplemented(): boolean {
    return this.kind === 'not_implemented' || (this.kind === 'not_found' && this.status === 404);
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === 'AbortError';
}

const KIND_LABELS: Readonly<Record<ApiErrorKind, string>> = {
  network: '无法连接服务',
  unauthorized: '登录状态失效',
  forbidden: '没有访问权限',
  not_found: '未找到相关内容',
  not_implemented: '功能暂未开放',
  conflict: '数据冲突',
  validation: '提交的内容不符合要求',
  rate_limited: '请求过于频繁',
  server: '服务暂时不可用',
  invalid_response: '数据暂时无法显示',
  unknown: '未知错误',
};

export function apiErrorTitle(error: unknown): string {
  if (isApiError(error)) {
    return KIND_LABELS[error.kind];
  }
  return '未知错误';
}

const HAS_CJK = /[一-鿿]/;
/** A request line such as "POST /alerts/x" belongs in logs, not in front of users. */
const REQUEST_LINE = /\b(GET|HEAD|POST|PUT|PATCH|DELETE|OPTIONS)\s+\//;

/**
 * True for a server message written for users: Chinese, and free of request lines
 * (the transport's own fallback reads "POST /x 返回 409").
 */
export function isUserFacingMessage(message: string): boolean {
  const text = message.trim();
  return HAS_CJK.test(text) && !REQUEST_LINE.test(text);
}

function userMessage(message: string, fallback: string): string {
  return isUserFacingMessage(message) ? message.trim() : fallback;
}

/** The request id is the one technical detail worth showing: support can trace it. */
function withRequestId(error: ApiError, sentence: string): string {
  return error.requestId === undefined
    ? `${sentence}。`
    : `${sentence}；如持续出现，请联系管理员并提供请求编号 ${error.requestId}。`;
}

/**
 * Turns any thrown value into a user-readable Chinese message plus the concrete
 * next step. Request methods, paths and configuration names stay out of the text;
 * a missing feature is still reported as unavailable, never as empty data.
 */
export function explainApiError(error: unknown): { title: string; detail: string } {
  if (!isApiError(error)) {
    return {
      title: '未知错误',
      detail: '发生了未预期的问题，请刷新页面后重试；如持续出现，请联系管理员。',
    };
  }

  const title = KIND_LABELS[error.kind];
  switch (error.kind) {
    case 'network':
      return {
        title,
        detail: '暂时无法连接到服务，请检查网络连接后重试；如果网络正常仍无法访问，请联系管理员。',
      };
    case 'not_implemented':
      return {
        title,
        detail: '该功能当前不可用，可能尚未开放或正在维护，请稍后再试或联系管理员。',
      };
    case 'not_found':
      return {
        title,
        detail: '要查看的内容可能已被删除，或不属于当前租户。请刷新页面，或切换租户后重试。',
      };
    case 'unauthorized':
      return { title, detail: '会话已过期或被撤销，请重新登录。' };
    case 'forbidden':
      return {
        title,
        detail: '当前账号在所选租户下没有查看或操作这部分内容的权限，如需开通请联系租户管理员。',
      };
    case 'validation':
      return { title, detail: userMessage(error.message, '请检查填写的内容后重试。') };
    case 'conflict':
      return {
        title,
        detail: userMessage(error.message, '该内容已被其他人修改或状态已变化，请刷新后重试。'),
      };
    case 'rate_limited':
      return { title, detail: '操作过于频繁，请稍后再试。' };
    case 'invalid_response':
      return {
        title,
        detail: withRequestId(
          error,
          '服务返回的数据暂时无法识别，可能是系统正在升级，请刷新页面重试',
        ),
      };
    case 'server':
      return { title, detail: withRequestId(error, '服务处理请求时出现异常，请稍后重试') };
    default:
      return { title, detail: withRequestId(error, '操作未完成，请稍后重试') };
  }
}

export function kindFromStatus(status: number): ApiErrorKind {
  if (status === 400 || status === 422) return 'validation';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 405 || status === 501) return 'not_implemented';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}
